import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Badge } from "@shared/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import {
  Check,
  AlertCircle,
  FileText,
  MapPin,
  Package,
  Edit,
  X,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { StepHelpers } from "@shared/components/Stepper";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateConsumer } from "@features/cx/consumerManagement/hooks";

interface ApplicationReviewFormProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stopTracking?: () => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export function ApplicationReviewForm({ 
    remoteUtilityId, 
    storageKey, 
    logTimeUntilStep,
    stopTracking,
    stepHelpers,
    currentStepIndex = 4, 
    onNext, 
    onPrevious, 
    setValidationError, 
    clearValidationError, 
    isValidating 
}: ApplicationReviewFormProps) {
  const [formData, setFormData] = useState<any>({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const dataLoadedRef = useRef(false);
  const { consumerId } = useParams<{ consumerId: string }>();
  const editConsumerMutation = useUpdateConsumer();
  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log(
          "Application review step data saved via stepHelpers:",
          stepData
        );
      } catch (error) {
        console.error(
          "Failed to save application review step data via stepHelpers:",
          error
        );
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      console.log(
        `Getting data for step ${currentStepIndex}`,
        stepHelpers.getStepData(currentStepIndex)
      );
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error(
        "Failed to get application review step data via stepHelpers:",
        error
      );
      return null;
    }
  }, [stepHelpers, currentStepIndex]);


  const getAllStepsData = useCallback(() => {
  if (!stepHelpers) return {};

  try {
    const stepProgressData = JSON.parse(
      localStorage.getItem(`edit-application-progress-${consumerId}`) || "{}"
    );

    const stepData = stepProgressData?.stepData || {};

    // Try to get step 0 data
    let step0Data = stepData[0] || {};

    // ✅ Fallback to localStorage backup if step0Data is empty
    if (Object.keys(step0Data).length === 0) {
      const fallbackPayload = JSON.parse(
        localStorage.getItem("consumerFirstStepData") || "{}"
      );
      const fallbackPrimary = fallbackPayload?.consumer_details?.primaryConsumer || {};
      const fallbackSecondary = fallbackPayload?.consumer_details?.secondaryConsumer || {};

      step0Data = {
        ...fallbackPrimary,
        hasSecondary: !!fallbackSecondary?.email,
        secondaryFirstName: fallbackSecondary.firstName,
        secondaryLastName: fallbackSecondary.lastName,
        secondaryEmail: fallbackSecondary.email,
        secondaryPhoneNumber: fallbackSecondary.contactNumber,
        primaryPhoneNumber: fallbackPrimary.contactNumber,

        secondaryRelationship: fallbackSecondary.secondaryRelationship,
      };
    }

    const step1Data = stepData[1] || {};
    const step2Data = stepData[2] || {};
    const step3Data = stepData[3] || {};

    const allStepsData = {
      consumerDetails: {
        firstName: step0Data.firstName,
        lastName: step0Data.lastName,
        phoneNumber: step0Data.phoneNumber ||step0Data.contactNumber ,
        email: step0Data.email,
        category: step0Data.category,
        categoryDisplay: step0Data.categoryDisplay,
        subCategory: step0Data.subCategory,
        subCategoryDisplay: step0Data.subCategoryDisplay,
        isVip: step0Data.isVip,
        vipLevel: step0Data.vipLevel,
        hasSecondary: step0Data.hasSecondary,
        secondaryFirstName: step0Data.secondaryFirstName,
        secondaryLastName: step0Data.secondaryLastName,
        secondaryPhoneNumber: step0Data.secondaryPhoneNumber,
        secondaryEmail: step0Data.secondaryEmail,
        secondaryRelationship: step0Data.secondaryRelationship,
        emailVerified: step0Data.emailVerified,
        lastUpdated: step0Data.lastUpdated,
        timestamp: step0Data.timestamp,
      },
      addressDetails: {
        houseNumber: step1Data.houseNumber,
        streetAddress: step1Data.streetAddress,
        city: step1Data.city,
        area: step1Data.area,
        subArea: step1Data.subArea,
        zipCode: step1Data.zipCode,
        premiseName: step1Data.premiseName,
        additionalInfo: step1Data.additionalInfo,
        sameAsBilling: step1Data.sameAsBilling,
        billingHouseNumber: step1Data.billingHouseNumber,
        billingStreetAddress: step1Data.billingStreetAddress,
        billingCity: step1Data.billingCity,
        billingArea: step1Data.billingArea,
        billingSubArea: step1Data.billingSubArea,
        billingZipCode: step1Data.billingZipCode,
        billingPremiseName: step1Data.billingPremiseName,
        serviceAvailable: step1Data.serviceAvailable,
        lastUpdated: step1Data.lastUpdated,
        timestamp: step1Data.timestamp,
        premiseNameDisplay: step1Data.premiseNameDisplay,
        billingPremiseNameDisplay: step1Data.billingPremiseNameDisplay,
      },
      documents: {
        documents: step2Data.documents || [],
        totalFileCount: step2Data.totalFileCount || 0,
        validFileCount: step2Data.validFileCount || 0,
        isStepComplete: step2Data.isStepComplete || false,
        completedAt: step2Data.completedAt,
        submittedAt: step2Data.submittedAt,
        timestamp: step2Data.timestamp,
      },
      planSelection: {
        planId: step3Data.planId,
        planName: step3Data.planName,
        selectedPlan: step3Data.selectedPlan,
        category: step3Data.category,
        subcategory: step3Data.subcategory,
        description: step3Data.description,
        billingCycle: step3Data.billingCycle,
        billingFrequency: step3Data.billingFrequency,
        billingFrequencyDisplay: step3Data.billingFrequencyDisplay,
        utilityServices: step3Data.utilityServices || [],
        utilityServiceData: step3Data.utilityServiceData,
        processingFees: step3Data.processingFees,
        connectionFee: step3Data.connectionFee,
        securityDeposit: step3Data.securityDeposit,
        tax: step3Data.tax,
        taxType: step3Data.taxType,
        taxTypeDisplay: step3Data.taxTypeDisplay,
        validity: step3Data.validity,
        total: step3Data.total,
        timestamp: step3Data.timestamp,
      },
      reviewData: stepHelpers.getStepData(4) || {},
    };

    return allStepsData;
  } catch (error) {
    console.error("Failed to get all step data via stepHelpers:", error);
    return {};
  }
}, [stepHelpers, consumerId]);



  // Load saved data from stepHelpers on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        console.log("Loading application review data from stepHelpers...");

        // Get all step data for review
        const allStepsData = getAllStepsData();
        const savedStepData = getStepData();

        console.log("Loaded all step data from stepHelpers:", allStepsData);

        // Combine all data for review
        const combinedData = {
          ...allStepsData,
          reviewData: savedStepData || {},
        };

        console.log('Combined data for review:', combinedData); 
        setFormData(combinedData);

        dataLoadedRef.current = true;
        setIsDataLoaded(true);
      } catch (error) {
        console.error("Failed to load step data from stepHelpers:", error);
        dataLoadedRef.current = true;
        setIsDataLoaded(true);
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    getStepData,
    getAllStepsData,
    saveStepData,
  ]);
  console.log("formData", formData);
  const consumerDetails = formData.consumerDetails || {};
  const addressDetails = formData.addressDetails || {};
  const documentsData = formData.documents || {};
  const documents = documentsData.documents || [];
  const planSelection = formData.planSelection || {};
  console.log("consumerDetails data is here man", consumerDetails);

  const allStepsData = (getAllStepsData() as {
  planSelection?: any;
  consumerDetails?: any;
  addressDetails?: any;
  documents?: any;
  reviewData?: any;
}) || { planSelection: {} };

const consumerBackup = allStepsData.consumerDetails || {};
console.log('consumerBackup', consumerBackup);


  const navigate = useNavigate();
  // Check if all required sections are completed
  const isSectionComplete = (section: string) => {
    switch (section) {
      case "consumer":
        return consumerDetails.firstName && consumerDetails.lastName;
      case "address":
        return addressDetails.streetAddress && addressDetails.city;
      case "documents":
        return documents.length > 0;
      case "plan":
        return !!planSelection.planId;
      default:
        return false;
    }
  };

  // Get missing fields for each section
  const getMissingFields = (section: string) => {
    switch (section) {
      case "consumer":
        const consumerMissing = [];
        if (!consumerDetails.firstName) consumerMissing.push("First Name");
        if (!consumerDetails.lastName) consumerMissing.push("Last Name");
        if (!consumerDetails.phoneNumber) consumerMissing.push("Phone Number");
        if (!consumerDetails.email) consumerMissing.push("Email");
        if (!consumerDetails.category) consumerMissing.push("Category");
        return consumerMissing;

      case "address":
        const addressMissing = [];
        if (!addressDetails.streetAddress)
          addressMissing.push("Street Address");
        if (!addressDetails.city) addressMissing.push("City");
        if (!addressDetails.zipCode) addressMissing.push("ZIP Code");
        return addressMissing;

      case "documents":
        return documents.length === 0 ? ["Required Documents"] : [];

      case "plan":
        return !planSelection.planId ? ["Plan Selection"] : [];

      default:
        return [];
    }
  };
  const handleSubmit = () => {
    console.log("planSelection at component level", planSelection);
    const totalDue =
      parseFloat(planSelection.applicationFee || 0) +
      parseFloat(planSelection.serviceConnectionFee || 0) +
      parseFloat(planSelection.securityDeposit || 0);
    let navigateTo = "/cx/consumer-onboarding/";
    let isNscNew = "1";
    if (totalDue > 0) {
      isNscNew = "0";
      navigateTo = `/cx/consumer-management/manual/success/${consumerId}`;
    }
    editConsumerMutation.mutate(
      {
        consumerId: consumerId,
        payload: {
          isNscNew: isNscNew,
          isReviewStep: "1",
        },
      },
      {
        onSuccess: (response) => {
          // Navigate with state data after successful mutation
          navigate(navigateTo, {
            state: {
              formData: formData,
              consumerId: consumerId,
              totalDue: totalDue,
              planSelection: formData?.planSelection?.selectedPlan,
              consumerNo: formData?.consumerNo, // Use from formData instead of response
              isNscNew: isNscNew,
              // Include the entire response object
              apiResponse: response,
            },
          });
          logTimeUntilStep('Application Review');
        },
        onError: (error) => {
          console.error("Consumer update failed:", error);
          // Handle error case - maybe show toast or error message
        },
      }
    );
    // Reset step data after successful submission
    stepHelpers?.resetAllData();
  };
  // Status badges for each section
  const SectionStatus = ({
    isComplete,
    missingFields = [],
  }: {
    isComplete: boolean;
    missingFields?: string[];
  }) => {
    if (isComplete) {
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
        >
          <Check className="h-3 w-3 mr-1" />
          Complete
        </Badge>
      );
    } else {
      return (
        <Badge
          variant="outline"
          className="bg-amber-50 text-amber-700 border-amber-200"
        >
          <AlertCircle className="h-3 w-3 mr-1" />
          {missingFields.length > 0
            ? `Missing ${missingFields.length} field${
                missingFields.length > 1 ? "s" : ""
              }`
            : "Incomplete"}
        </Badge>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* <Card className="border-0 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start">
            <div className="ml-4">
              <h3 className="text-lg font-medium text-blue-900">
                Application Review
              </h3>
              <p className="text-sm text-blue-700">
                Please review all sections carefully. Make sure all required
                information is provided. Missing information is highlighted
                below each section.
              </p>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Application Review Alert */}
<Accordion type="single" collapsible className="mb-6">
  <AccordionItem
    value="application-review"
    className="border border-blue-200 rounded-lg bg-blue-50"
  >
    <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
      <div className="flex items-center">
        <AlertCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
        <span className="font-medium text-blue-900">
          Application Review
        </span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="px-4 pb-4">
      <p className="text-blue-700 text-sm">
        Please review all sections carefully. Make sure all required
        information is provided. Missing information is highlighted
        below each section.
      </p>
    </AccordionContent>
  </AccordionItem>
</Accordion>


      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1 pb-4">
              <CardTitle className="card-heading-text">Consumer Details</CardTitle>
              <CardDescription className="card-subheading-text">
                Primary and secondary consumer information
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <SectionStatus
                isComplete={isSectionComplete("consumer")}
                missingFields={getMissingFields("consumer")}
              />
              <Button variant="outline" size="sm" >
                <a href="#consumer-details">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Primary Consumer</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="font-medium">First Name:</span>
                  {consumerDetails.firstName || (
                    <span className="text-red-500 flex items-center">
                      <X className="h-4 w-4 mr-1" /> Missing
                    </span>
                  )}
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Last Name:</span>
                  {consumerDetails.lastName || (
                    <span className="text-red-500 flex items-center">
                      <X className="h-4 w-4 mr-1" /> Missing
                    </span>
                  )}
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Phone:</span>
                  {consumerDetails.phoneNumber || (
                    <span className="text-red-500 flex items-center">
                      <X className="h-4 w-4 mr-1" /> Missing
                    </span>
                  )}
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Email:</span>
                  {consumerDetails.email || (
                    <span className="text-red-500 flex items-center">
                      <X className="h-4 w-4 mr-1" /> Missing
                    </span>
                  )}
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Category:</span>
                  {consumerDetails.categoryDisplay || (
                    <span className="text-red-500 flex items-center">
                      <X className="h-4 w-4 mr-1" /> Missing
                    </span>
                  )}
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Sub-category:</span>
                  {consumerDetails.subCategoryDisplay || "N/A"}
                </li>
              </ul>
              {consumerDetails.isVip && (
                <div className="mt-3">
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">
                    VIP: {consumerDetails.isVip ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
            </div>

            {consumerDetails.hasSecondary && (
              <div>
                <h4 className="font-medium mb-2">Secondary Consumer</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="font-medium">First Name:</span>
                    {consumerDetails.secondaryFirstName || "N/A"}
                  </li>
                  <li className="flex justify-between">
                    <span className="font-medium">Last Name:</span>
                    {consumerDetails.secondaryLastName || "N/A"}
                  </li>
                  <li className="flex justify-between">
                    <span className="font-medium">Phone:</span>
                    {consumerDetails.secondaryPhoneNumber || "N/A"}
                  </li>
                  <li className="flex justify-between">
                    <span className="font-medium">Email:</span>
                    {consumerDetails.secondaryEmail || "N/A"}
                  </li>
                  <li className="flex justify-between">
                    <span className="font-medium">Relationship:</span>
                    {consumerDetails.secondaryRelationship || "N/A"}
                  </li>
                </ul>
              </div>
            )}
          </div>

          {!isSectionComplete("consumer") && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-md text-red-700 text-sm">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                <strong>Missing Information:</strong>
              </div>
              <ul className="list-disc ml-6 mt-1">
                {getMissingFields("consumer").map((field, index) => (
                  <li key={index}>{field}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1 pb-4">
              <CardTitle className="card-heading-text">Address Details</CardTitle>
              <CardDescription className="card-subheading-text">Service and billing locations</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <SectionStatus
                isComplete={isSectionComplete("address")}
                missingFields={getMissingFields("address")}
              />
              <Button variant="outline" size="sm" >
                <a href="#address-details">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2 flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-primary" />
                Service Address
              </h4>
              <div className="p-3 bg-slate-50 rounded-md mb-3">
                <p className="text-sm">
                  {addressDetails.houseNumber || "No."}{" "}
                  {addressDetails.streetAddress || "Street Address Missing"},{" "}
                  {addressDetails.city || "City Missing"}
                  {addressDetails.area ? `, ${addressDetails.area}` : ""}{" "}
                  {addressDetails.zipCode || "Zip Missing"}
                </p>
              </div>
              {addressDetails.additionalInfo && (
                <div className="mt-3 text-sm">
                  <span className="font-medium">Additional Instructions:</span>
                  <p className="mt-1 text-muted-foreground">
                    {addressDetails.additionalInfo}
                  </p>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-medium mb-2 flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-primary" />
                Billing Address
              </h4>
              <div className="p-3 bg-slate-50 rounded-md mb-3">
                <p className="text-sm">
                  {addressDetails.billingHouseNumber || "No."}{" "}
                  {addressDetails.billingStreetAddress || "Street Address Missing"},{" "}
                  {addressDetails.billingCity || "City Missing"}
                  {addressDetails.billingArea ? `, ${addressDetails.area}` : ""}{" "}
                  {addressDetails.billingZipCode || "Zip Missing"}
                </p>
              </div>
            </div>
          </div>

          {!isSectionComplete("address") && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-md text-red-700 text-sm">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                <strong>Missing Information:</strong>
              </div>
              <ul className="list-disc ml-6 mt-1">
                {getMissingFields("address").map((field, index) => (
                  <li key={index}>{field}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1 pb-4">
              <CardTitle className="card-heading-text">Documents</CardTitle>
              <CardDescription className="card-subheading-text">Uploaded verification documents</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <SectionStatus
                isComplete={isSectionComplete("documents")}
                missingFields={getMissingFields("documents")}
              />
              <Button variant="outline" size="sm" >
                <a href="#document-upload">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {documents.map((doc: any) => (
                  <div
                    key={doc.id}
                    className="border rounded p-3 flex flex-col items-center text-center"
                  >
                    <FileText className="h-10 w-10 text-blue-500 mb-2" />
                    <div className="font-medium text-sm truncate max-w-full">
                      {doc.name || doc.id}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {doc.type || "Document"}
                    </div>
                    <div className="mt-2 flex items-center text-green-600 text-xs">
                      <Check className="h-3 w-3 mr-1" />
                      Uploaded
                    </div>
                  </div>
                ))}
              </div>

              {/* Document upload summary */}
              <div className="mt-4 p-3 bg-green-50 border border-green-100 rounded-md">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">
                    <strong>Upload Summary:</strong>{" "}
                    {documentsData.validFileCount || documents.length} of{" "}
                    {documentsData.totalFileCount || documents.length} files
                    uploaded successfully
                  </span>
                  {documentsData.isStepComplete && (
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-200"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Complete
                    </Badge>
                  )}
                </div>
                {documentsData.completedAt && (
                  <p className="text-xs text-green-600 mt-1">
                    Completed:{" "}
                    {new Date(documentsData.completedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="p-3 bg-red-50 border border-red-100 rounded-md text-red-700 text-sm">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                <strong>Missing Documents:</strong>
              </div>
              <p className="mt-1">
                Required documentation must be uploaded before proceeding.
              </p>
              <Button size="sm" variant="outline" className="mt-2" >
                <a href="#document-upload">
                  <FileText className="h-3 w-3 mr-1" />
                  Upload Documents
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1 pb-4">
              <CardTitle className="card-heading-text">Selected Plan</CardTitle>
              <CardDescription className="card-subheading-text">
                Service plan and utility services
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <SectionStatus
                isComplete={isSectionComplete("plan")}
                missingFields={getMissingFields("plan")}
              />
              <Button variant="outline" size="sm" >
                <a href="#plan-selection">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {planSelection.planId ? (
            <div className="space-y-6">
              <div className="p-4 border rounded-md">
                <div className="flex items-center gap-4">
                  <Package className="h-10 w-10 text-primary" />
                  <div>
                    <h4 className="font-medium">
                      {planSelection.planName ||
                        planSelection.selectedPlan?.planName ||
                        "Selected"}{" "}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {planSelection.billingFrequencyDisplay ||
                        planSelection.billingCycle ||
                        "Monthly"}{" "}
                      billing
                    </p>
                  </div>
                </div>

                {planSelection.description && (
                  <p className="mt-3 text-sm">{planSelection.description}</p>
                )}

                {/* Plan Details */}
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Category:</span>
                      <span className="ml-2">
                        {planSelection.category || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Subcategory:</span>
                      <span className="ml-2">
                        {planSelection.selectedPlan.subcategory || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Connection Fee:</span>
                      <span className="ml-2">
                        ${planSelection.connectionFee || 0}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Security Deposit:</span>
                      <span className="ml-2">
                        ${planSelection.securityDeposit || 0}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Processing Fees:</span>
                      <span className="ml-2">
                        ${planSelection.processingFees || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {planSelection.utilityServices &&
                  planSelection.utilityServices.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium mb-2">
                        Utility Services:
                      </h5>
                      <div className="space-y-2">
                        {planSelection.utilityServices.map(
                          (service: any, index: number) => (
                            <div
                              key={index}
                              className="flex justify-between text-sm p-2 bg-gray-50 rounded"
                            >
                              <span className="font-medium">{service}</span>
                              <span className="text-muted-foreground">
                                {
                                  planSelection.utilityServiceData?.rate?.[
                                    service
                                  ]?.rate?.rateName
                                }
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {planSelection.utilityServiceData && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium mb-2">
                      Service Details:
                    </h5>
                    <div className="space-y-2">
                      {Object.entries(
                        planSelection.utilityServiceData.rate || {}
                      ).map(([service, data]: [string, any]) => (
                        <div key={service} className="p-2 bg-gray-50 rounded">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{service}</span>
                          </div>
                          {data.estimatedAvgBillAmount && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Est. Avg: ${data.estimatedAvgBillAmount}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-3 bg-red-50 border border-red-100 rounded-md text-red-700 text-sm">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                <strong>No Plan Selected:</strong>
              </div>
              <p className="mt-1">
                You must select a service plan before proceeding.
              </p>
              <Button size="sm" variant="outline" className="mt-2" >
                <a href="#plan-selection">
                  <Package className="h-3 w-3 mr-1" />
                  Select a Plan
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <Button
            onClick={onPrevious}
            variant="outline"
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </Button>
        </div>
        <Button
          onClick={handleSubmit}
          className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span>Submit Application</span>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}
